import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, Filter, Download, Eye, EyeOff } from 'lucide-react';
import { statuses as statusService } from '../../services/apiService';
import { Loader } from '../UI/LoadingSpinner';
import { getTalentTypeLabel, getTalentTypeColor } from '../../utils/talentTypeUtils';
import { Autocomplete } from '@anyemp/ui-kit';
import ResponsiveTableFrame from '../UI/ResponsiveTableFrame';

const TransitionGraph = ({ talentType = null, onStatusClick = null }) => {
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTalentType, setSelectedTalentType] = useState(talentType);
  const [showProperties, setShowProperties] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    loadGraphData();
  }, [selectedTalentType]);

  const loadGraphData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await statusService.getTransitionGraph(selectedTalentType);
      setGraphData(response.data);
    } catch (error) {
      console.error('Error loading transition graph:', error);
      setError('Error loading transition graph: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    if (onStatusClick) {
      onStatusClick(node);
    }
  };

  const handleDownloadSVG = () => {
    if (!svgRef.current) return;
    
    const svgData = new XMLSerializer().serializeToString(svgRef.current);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    const downloadLink = document.createElement('a');
    downloadLink.href = svgUrl;
    downloadLink.download = `status-transitions-${selectedTalentType || 'all'}.svg`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(svgUrl);
  };

  const renderGraph = () => {
    if (!graphData.nodes.length) return null;

    const width = 800;
    const height = 600;
    const nodeRadius = 30;
    const padding = 50;

    // Simple layout algorithm - arrange nodes in a circle
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2 - padding;

    const nodes = graphData.nodes.map((node, index) => {
      const angle = (2 * Math.PI * index) / graphData.nodes.length;
      return {
        ...node,
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle)
      };
    });

    return (
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800"
      >
        {/* Edges */}
        {graphData.edges.map((edge) => {
          const fromNode = nodes.find(n => n.id === edge.from);
          const toNode = nodes.find(n => n.id === edge.to);
          
          if (!fromNode || !toNode) return null;

          const dx = toNode.x - fromNode.x;
          const dy = toNode.y - fromNode.y;
          const length = Math.sqrt(dx * dx + dy * dy);
          const unitX = dx / length;
          const unitY = dy / length;

          const startX = fromNode.x + unitX * nodeRadius;
          const startY = fromNode.y + unitY * nodeRadius;
          const endX = toNode.x - unitX * nodeRadius;
          const endY = toNode.y - unitY * nodeRadius;

          return (
            <g key={`${edge.from}-${edge.to}`}>
              {/* Arrow line */}
              <line
                x1={startX}
                y1={startY}
                x2={endX}
                y2={endY}
                stroke={edge.requires_approval ? '#f59e0b' : '#6b7280'}
                strokeWidth={edge.requires_approval ? 3 : 2}
                strokeDasharray={edge.transition_reason_required ? '5,5' : 'none'}
                markerEnd="url(#arrowhead)"
              />
              
              {/* Edge label */}
              <text
                x={(startX + endX) / 2}
                y={(startY + endY) / 2 - 5}
                textAnchor="middle"
                className="text-xs fill-gray-600 dark:fill-gray-400"
              >
                {edge.requires_approval && '🔒'}
                {edge.transition_reason_required && '📝'}
              </text>
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map((node) => (
          <g key={node.id}>
            <circle
              cx={node.x}
              cy={node.y}
              r={nodeRadius}
              fill={node.color || '#6b7280'}
              stroke={selectedNode?.id === node.id ? '#3b82f6' : '#374151'}
              strokeWidth={selectedNode?.id === node.id ? 3 : 1}
              className="cursor-pointer hover:stroke-blue-500 transition-colors"
              onClick={() => handleNodeClick(node)}
            />
            
            {/* Node label */}
            <text
              x={node.x}
              y={node.y + 5}
              textAnchor="middle"
              className="text-xs font-medium fill-white pointer-events-none"
            >
              {node.name.length > 8 ? node.name.substring(0, 8) + '...' : node.name}
            </text>
            
            {/* Talent type indicator */}
            <circle
              cx={node.x + nodeRadius - 8}
              cy={node.y - nodeRadius + 8}
              r={6}
              fill={getTalentTypeColor(node.talent_types?.[0] || node.type).includes('bg-') ? 
                getTalentTypeColor(node.talent_types?.[0] || node.type).replace('bg-', '') : '#3b82f6'}
              className="pointer-events-none"
            />
          </g>
        ))}

        {/* Arrow marker definition */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon
              points="0 0, 10 3.5, 0 7"
              fill="#6b7280"
            />
          </marker>
        </defs>
      </svg>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Status Transition Graph
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Visualization of transitions between statuses
          </p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <Autocomplete
            clearable
            options={[
              { value: 'candidate', label: 'Candidates' },
              { value: 'trainee', label: 'Trainees' },
              { value: 'employee', label: 'Employees' },
              { value: 'presale', label: 'Pre-sale' }
            ]}
            value={selectedTalentType || ''}
            onChange={(value) => setSelectedTalentType(value || null)}
            placeholder="Select talent type..."
          />
          
          <button
            onClick={() => setShowProperties(!showProperties)}
            className={`p-2 rounded-md transition-colors ${
              showProperties 
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' 
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
            title="Show Properties"
          >
            {showProperties ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
          
          <button
            onClick={handleDownloadSVG}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Download SVG"
          >
            <Download className="w-4 h-4" />
          </button>
          
          <button
            onClick={loadGraphData}
            disabled={loading}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Legend */}
      {showProperties && (
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Legend:
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-amber-500"></div>
              <span className="text-gray-600 dark:text-gray-400">Requires approval</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-gray-500 border-dashed border-t-2 border-gray-500"></div>
              <span className="text-gray-600 dark:text-gray-400">Requires reason</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs">🔒</span>
              <span className="text-gray-600 dark:text-gray-400">Approval</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs">📝</span>
              <span className="text-gray-600 dark:text-gray-400">Reason</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-gray-600 dark:text-gray-400">Talent type</span>
            </div>
          </div>
        </div>
      )}

      {/* Graph stays comparative: contain its intentional horizontal scroll. */}
      <ResponsiveTableFrame scrollLabel="Status transition graph" tableClassName="pb-2">
      <div className="flex min-w-max justify-center">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader variant="component" size="md" fullHeight={false} />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <button
              onClick={loadGraphData}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : graphData.nodes.length === 0 ? (
          <div className="text-center py-12">
            <Filter className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">
              No transitions to display
            </p>
          </div>
        ) : (
          renderGraph()
        )}
      </div>
      </ResponsiveTableFrame>

      {/* Selected Node Info */}
      {selectedNode && (
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
            Selected Status: {selectedNode.name}
          </h4>
          <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
            <div>
              <span className="text-blue-700 dark:text-blue-300">Type:</span>
              <span className="ml-2 text-blue-900 dark:text-blue-100">
                {((selectedNode.talent_types || []).length > 0 ? selectedNode.talent_types : [selectedNode.type])
                  .map(getTalentTypeLabel)
                  .join(', ')}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Description:</span>
              <span className="ml-2 text-blue-900 dark:text-blue-100">
                {selectedNode.description || 'Not specified'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="mt-6 grid grid-cols-1 gap-4 text-sm text-gray-600 dark:text-gray-400 sm:grid-cols-2">
        <div>
          <span className="font-medium">Statuses:</span> {graphData.nodes.length}
        </div>
        <div>
          <span className="font-medium">Transitions:</span> {graphData.edges.length}
        </div>
      </div>
    </div>
  );
};

export default TransitionGraph;
